import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useCommunity } from "@contexts/CommunityContext";

import api from "@helpers/api";
import {
  adaptProgram,
  adaptEnrollmentListItem,
} from "../../helpers/apiAdapters";

import { DEFAULT_ENROLLMENT_SORT } from "../../enums/enrollmentSorts";
import {
  DEFAULT_ENROLLMENT_TAB,
  ENROLLMENT_TAB_STATUS,
} from "../../enums/enrollmentTabs";

const PER_PAGE = 15;
const INITIAL_FILTERS = { status: [], source: [] };
const VALID_TABS = new Set(Object.keys(ENROLLMENT_TAB_STATUS));

export default function useEnrollments(programId) {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { community, loading: communityLoading } = useCommunity();
  const currentCommunity = community.getBySlug(slug);
  const communityId = currentCommunity?.id;

  // Program header (§6.3).
  const [program, setProgram] = useState(null);
  const [programLoading, setProgramLoading] = useState(true);

  // Enrollments list.
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [records, setRecords] = useState(0);

  // Active tab is mirrored in the URL (?tab=) so it survives refresh/share and
  // browser back/forward. The query param is the single source of truth; an
  // unknown/absent value falls back to the default tab.
  const tabParam = searchParams.get("tab");
  const activeTab = VALID_TABS.has(tabParam) ? tabParam : DEFAULT_ENROLLMENT_TAB;

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [sort, setSort] = useState(DEFAULT_ENROLLMENT_SORT);
  const [search, setSearch] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  // Pending-approvals card / tab badge (§6.3). Independent of the list filters.
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingLoading, setPendingLoading] = useState(true);

  const listRequestRef = useRef(0);

  // Fetch the program header once per program.
  useEffect(() => {
    if (communityLoading || !communityId || !programId) return;
    let alive = true;
    setProgramLoading(true);
    (async () => {
      const res = await api.get(`community/${communityId}/mentoring/programs/${programId}`);
      if (!alive) return;
      setProgram(res?.response ? null : adaptProgram(res?.data));
      setProgramLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [communityLoading, communityId, programId]);

  // Count of enrollments awaiting approval (drives the header card + tab badge).
  useEffect(() => {
    if (communityLoading || !communityId || !programId) return;
    let alive = true;
    setPendingLoading(true);
    (async () => {
      const { data } = await api.get(
        `community/${communityId}/mentoring/programs/${programId}/enrollments`,
        { status: ["pending"], page: 1, per_page: 1 },
      );
      if (!alive) return;
      setPendingCount(data?.meta?.total ?? 0);
      setPendingLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [communityLoading, communityId, programId, reloadToken]);

  // Fetch the list on tab / filter / sort / search / page changes.
  useEffect(() => {
    if (communityLoading || !communityId || !programId) return;

    const requestId = ++listRequestRef.current;
    setLoading(true);

    (async () => {
      try {
        const params = { sort, page, per_page: PER_PAGE };

        // The tab forces a status set; only the "all" tab honors the ChipPicker
        // status/source filters.
        const tabStatus = ENROLLMENT_TAB_STATUS[activeTab];
        const status = tabStatus ?? filters.status;
        if (status.length) params.status = status;
        if (activeTab === "all" && filters.source.length) params.source = filters.source;
        // `search` isn't supported by the backend yet (ClickUp 868jxwrh5) —
        // sent anyway so it starts working the moment the gap closes.
        if (search.trim()) params.search = search.trim();

        const { data } = await api.get(
          `community/${communityId}/mentoring/programs/${programId}/enrollments`,
          params,
        );

        if (requestId !== listRequestRef.current) return;
        setEnrollments(
          Array.isArray(data?.data)
            ? data.data.map((item) => adaptEnrollmentListItem(item, programId))
            : [],
        );
        setRecords(data?.meta?.total ?? 0);
        setPages(data?.meta?.last_page ?? 1);
      } catch (error) {
        if (requestId !== listRequestRef.current) return;
        console.error("Failed to load enrollments", error);
        setEnrollments([]);
        setRecords(0);
        setPages(1);
      } finally {
        if (requestId === listRequestRef.current) setLoading(false);
      }
    })();
  }, [communityLoading, communityId, programId, activeTab, filters, sort, search, page, reloadToken]);

  const changeTab = useCallback(
    (tab) => {
      setPage(1);
      // Drop the param for the default tab to keep canonical URLs clean; replace
      // (not push) so flipping tabs doesn't stack history entries.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!tab || tab === DEFAULT_ENROLLMENT_TAB) next.delete("tab");
          else next.set("tab", tab);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setStatusFilter = useCallback((status) => {
    setFilters((prev) => ({ ...prev, status }));
    setPage(1);
  }, []);

  const setSourceFilter = useCallback((source) => {
    setFilters((prev) => ({ ...prev, source }));
    setPage(1);
  }, []);

  const changeSort = useCallback((value) => {
    setSort(value);
    setPage(1);
  }, []);

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
  }, []);

  const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

  // Apply a program edit (from the editor) to the header without a refetch.
  const applyProgramUpdate = useCallback((updated) => {
    if (updated?.id) setProgram(updated);
  }, []);

  const hasFilters = filters.status.length > 0 || filters.source.length > 0;

  return {
    currentCommunity,
    program,
    programLoading,
    enrollments,
    loading,
    page,
    pages,
    records,
    activeTab,
    filters,
    sort,
    search,
    hasFilters,
    pendingCount,
    pendingLoading,
    changeTab,
    setStatusFilter,
    setSourceFilter,
    changeSort,
    handleSearch,
    clearFilters,
    setPage,
    refresh,
    applyProgramUpdate,
  };
}
